import { createConsultant, updateUser } from '@/api/admin';
import { SvgIcon } from '@/components/icon';
import ImageCropper from '@/components/image-croper';
import { AvatarPlaceholder } from '@/components/shared/avatar-placeholder';
import { blobToFile } from '@/helpers/blobToFile';
import { getBase64 } from '@/helpers/getBase64';
import { Consultant, UserRole } from '@/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getGroups } from '@/api/groups';
import industries from '@/industries.json';
import {
  Button,
  Flex,
  Form,
  FormInstance,
  Input,
  message,
  Select,
  Typography,
  Upload,
  UploadFile,
  UploadProps,
  Image,
} from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { phoneRules } from '@/helpers/validation';

interface ConsultantEditProps {
  form: FormInstance<Partial<Consultant>>;
  onCancel: () => void;
}

export const ConsultantCreate = ({
  form,
  onCancel,
}: ConsultantEditProps): JSX.Element => {
  const [avatar, setAvatar] = useState<string>('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [imageToCrop, setImageToCrop] = useState<string>('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: groups = [] } = useQuery({
    queryKey: ['admin', 'groups'],
    queryFn: getGroups,
  });

  const experienceOptions: DefaultOptionType[] = [
    {
      value: 1,
      label: 'less than 1 year',
    },
    {
      value: 2,
      label: 'greater than 1 year',
    },
    {
      value: 3,
      label: 'greater than 3 years',
    },
    {
      value: 5,
      label: 'greater than 5 years',
    },
    {
      value: 10,
      label: 'greater than 10 years',
    },
  ];

  const validationRules = {
    required: (fieldName: string) => ({
      required: true,
      validator: (_: any, value: string) => {
        if (value && value.trim() !== '') {
          return Promise.resolve();
        }
        return Promise.reject(new Error(`${fieldName} is required`));
      },
    }),
    url: {
      pattern: /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})(\/\S*)?$/,
      message: 'Please enter a valid URL (with or without www)',
    },
    phone: phoneRules,
    email: {
      validator: (_: any, value: string) => {
        // Trim the value first
        const trimmedValue = value?.trim() || '';

        // Check if empty after trimming
        if (!trimmedValue) {
          return Promise.reject(new Error('Email is required'));
        }

        // Validate email format against trimmed value
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(trimmedValue)) {
          return Promise.reject(
            new Error('Please enter a valid email address'),
          );
        }
        return Promise.resolve();
      },
    },
  };

  const handleChangePhoto: UploadProps['onChange'] = async (info) => {
    const { fileList: newFileList, file } = info;
    try {
      if (file && file.originFileObj) {
        const isImage = file?.type && file.type.startsWith('image/');
        if (!isImage) {
          message.error('Only image files are allowed (JPG, PNG, JPEG).');
          return;
        }
      }
      if (info.file.status === 'done') {
        setImageToCrop(await getBase64(info.file.originFileObj));
      }

      if (info.file.status === 'removed') {
        setAvatar('');
      }

      setFileList(newFileList);
    } catch (error) {
      message.error('Failed to update profile. Please try again.');
    }
  };

  const uploadProps: UploadProps = {
    name: 'logo',
    fileList,
    multiple: false,
    maxCount: 1,
    onChange: handleChangePhoto,
    customRequest({ onSuccess }) {
      if (onSuccess) {
        onSuccess('ok');
      }
    },
    onRemove: () => {
      form.setFieldsValue({ profilePhoto: '' });
      setAvatar('');
    },
  };

  const consultantMutation = useMutation({
    mutationFn: createConsultant,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ['consultant'],
      });
      message.success('Consultant created successfully');
      navigate(`/control-panel/consultants/${response.id}`);
    },
    onError: (error: any) => {
      message.error(error.message || 'Something went wrong. Please try again.');
    },
  });

  const handleFinish = async (values: Partial<Consultant>) => {
    const data: Partial<Consultant> = {};

    Object.keys(values).forEach((key) => {
      const typedKey = key as keyof Consultant;
      const value = values[typedKey];
      if (value !== undefined) {
        (data as any)[typedKey] = value;
      }
    });

    const avatarFile = avatar.startsWith('blob:')
      ? await blobToFile(avatar, 'cropped-image.jpg')
      : undefined;

    consultantMutation.mutate(
      {
        data: {
          ...data,
          role: UserRole.CONSULTANT,
        },
        avatar: avatarFile,
      },
      {
        onSuccess: (data) => {
          setFileList([]);
        },
      },
    );
  };

  const handleFinishFailed = (errorInfo: any) => {
    message.error('Please fill in all required fields correctly');
    console.log('Validation failed:', errorInfo);
  };

  return (
    <Flex gap={24} flex={1}>
      <div className={'edit-wrapper'} style={{ flex: 1 }}>
        <Typography.Title level={4} style={{ margin: '0 0 24px' }}>
          General Info
        </Typography.Title>
        <Form
          form={form}
          layout="vertical"
          name="basic"
          onFinish={handleFinish}
          onFinishFailed={handleFinishFailed}
          className="admin-forgot-form"
        >
          <Flex justify="space-between" align="center" gap={24}>
            <Form.Item
              style={{ flex: 1 }}
              name="firstName"
              label={<label style={{ color: '#9FA1AD' }}>First Name</label>}
              rules={[validationRules.required('First name')]}
              required={false}
              validateTrigger={['onChange', 'onBlur']}
            >
              <Input placeholder="Enter here" className="edit-input" />
            </Form.Item>
            <Form.Item
              name="lastName"
              style={{ flex: 1 }}
              label={<label style={{ color: '#9FA1AD' }}>Last Name</label>}
              rules={[validationRules.required('Last name')]}
              required={false}
              validateTrigger={['onChange', 'onBlur']}
            >
              <Input placeholder="Enter here" className="edit-input" />
            </Form.Item>
          </Flex>
          <Flex justify="space-between" align="center" gap={24}>
            <Form.Item
              style={{ flex: 1 }}
              name="email"
              label={<label style={{ color: '#9FA1AD' }}>Email</label>}
              rules={[validationRules.email]}
              required={false}
              validateTrigger="onBlur"
            >
              <Input placeholder="Enter here" className="edit-input" />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name="phone"
              rules={validationRules.phone}
              label={<label style={{ color: '#9FA1AD' }}>Phone</label>}
            >
              <Input placeholder="Enter here" className="edit-input" />
            </Form.Item>
          </Flex>

          <div
            style={{
              height: 1,
              width: '100%',
              backgroundColor: '#D9D9D9',
              margin: '24px 0',
            }}
          />

          <Typography.Title level={4} style={{ margin: '0 0 24px' }}>
            Professional Info
          </Typography.Title>

          <Flex justify="space-between" align="center" gap={24}>
            <Form.Item
              style={{ flex: 1 }}
              name="profileUrl"
              label={<label style={{ color: '#9FA1AD' }}>Profile Url</label>}
            >
              <Input placeholder="Enter here" className="edit-input" />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name="experienceLevel"
              label={<label style={{ color: '#9FA1AD' }}>Experience</label>}
            >
              <Select
                showSearch
                options={experienceOptions}
                size={'large'}
                className="edit-select"
              />
            </Form.Item>
          </Flex>

          <Flex justify="space-between" align="center" gap={24}>
            <Form.Item
              style={{ flex: 1 }}
              name="industrySpecialty"
              label={
                <label style={{ color: '#9FA1AD' }}>Industry Specialty</label>
              }
            >
              <Select
                showSearch
                mode="multiple"
                maxTagCount={2}
                options={industries.data.map((i) => ({
                  label: i,
                  value: i,
                }))}
                size={'large'}
                className="edit-select"
              />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name="region"
              label={<label style={{ color: '#9FA1AD' }}>Region</label>}
            >
              <Select
                showSearch
                mode="multiple"
                maxTagCount={2}
                options={[
                  {
                    label: 'USA',
                    value: 'USA',
                  },
                  {
                    label: 'Canada',
                    value: 'Canada',
                  },
                  {
                    label: 'Europe',
                    value: 'Europe',
                  },
                  {
                    label: 'Middle East and North Africa',
                    value: 'Middle East and North Africa',
                  },
                  {
                    label: 'Asia',
                    value: 'Asia',
                  },
                  {
                    label: 'South and Central America',
                    value: 'South and Central America',
                  },
                ]}
                size={'large'}
                className="edit-select"
              />
            </Form.Item>
          </Flex>

          <Flex justify="space-between" align="center" gap={24}>
            <Form.Item
              name="bio"
              style={{ flex: 1 }}
              label={<label style={{ color: '#9FA1AD' }}>Bio</label>}
            >
              <Input.TextArea
                placeholder="Enter here"
                className="edit-input"
                style={{ height: '100px' }}
              />
            </Form.Item>
          </Flex>
          <Flex justify="space-between" align="center" gap={24}>
            <Form.Item
              name="groups"
              style={{ flex: 1 }}
              label={<label style={{ color: '#9FA1AD' }}>Broker Group</label>}
            >
              <Select
                showSearch
                mode="multiple"
                maxTagCount={2}
                className="edit-select"
                suffixIcon={
                  <SvgIcon style={{ zIndex: 100 }} type={'chevronDown'} />
                }
                options={groups.map((group) => ({
                  label: group.name,
                  value: group.id,
                }))}
                size={'large'}
                placeholder={'Type to search'}
              />
            </Form.Item>
          </Flex>

          <Form.Item style={{ marginBottom: 0 }}>
            <Flex align="center" gap={8}>
              <Button
                type="primary"
                className="edit-button"
                onClick={() => form.submit()}
                // loading={isPending}
                // disabled={isPending}
              >
                Save Changes
              </Button>
              <Button
                type="default"
                className="edit-button"
                onClick={onCancel}
                // loading={isPending}
                // disabled={isPending}
              >
                Cancel
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </div>
      <div className={'edit-wrapper'} style={{ height: '40%' }}>
        <label style={{ color: '#9FA1AD', paddingBottom: '24px' }}>Icon</label>
        {imageToCrop.length > 0 && (
          <ImageCropper
            imageToCrop={imageToCrop}
            setImageToCrop={setImageToCrop}
            setAvatar={setAvatar}
          />
        )}
        <Flex align={'center'} vertical style={{ margin: '0 auto' }}>
          <div style={{ position: 'relative' }}>
            {avatar ? (
              <>
                <Image
                  src={avatar}
                  wrapperClassName={'avatar'}
                  height={180}
                  width={180}
                  style={{ borderRadius: '50%' }}
                />
              </>
            ) : (
              <div
                style={{
                  width: 180,
                  height: 180,
                  overflow: 'hidden',
                  borderRadius: '50%',
                  fontSize: 36,
                }}
              >
                <AvatarPlaceholder firstName={''} lastName={''} />
              </div>
            )}
          </div>
          <Form.Item name={'profilePhoto'} label={'Profile Photo'} noStyle>
            <Upload {...uploadProps} accept=".jpg,.jpeg,.png">
              <Button type={'default'} className={'edit-upload-image'}>
                Change Photo
              </Button>
            </Upload>
          </Form.Item>
        </Flex>
      </div>
    </Flex>
  );
};
